import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Field, FormError, Label } from '@/components/ui/field';
import { useSavePreferences } from '@/hooks/clients';
import { ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast-context';
import { cn } from '@/lib/utils';
import {
  CONTACT_METHOD_LABELS,
  CONTACT_METHOD_VALUES,
  WEEKDAY_LABELS,
  type ClientDetail,
  type ContactMethodValue,
} from '@shared/schemas/client';

/**
 * The Preferences tab.
 *
 * Everything here feeds scheduling in phase 7 rather than being a note somebody
 * reads — preferred days and a preferred time are what auto-assignment scores
 * against, and pet notes are what a cleaner needs before they open a door.
 */
export function ClientPreferencesTab({ detail }: { detail: ClientDetail }) {
  const toast = useToast();
  const save = useSavePreferences(detail.id);

  const [days, setDays] = useState<number[]>(detail.preferences.preferredDays ?? []);
  // `time` columns come back as `HH:MM:SS` from the driver; an `<input
  // type="time">` wants `HH:MM`.
  const [time, setTime] = useState((detail.preferences.preferredTime ?? '').slice(0, 5));
  const [contactMethod, setContactMethod] = useState<ContactMethodValue | ''>(
    detail.preferences.preferredContactMethod ?? '',
  );
  const [requirements, setRequirements] = useState(detail.preferences.specialRequirements ?? '');
  const [pets, setPets] = useState(detail.preferences.petNotes ?? '');
  const [allergies, setAllergies] = useState(detail.preferences.allergyNotes ?? '');

  const error = save.error instanceof ApiError ? save.error : null;

  function toggleDay(day: number) {
    setDays((current) =>
      current.includes(day)
        ? current.filter((entry) => entry !== day)
        : [...current, day].sort((a, b) => a - b),
    );
  }

  function submit(event: FormEvent) {
    event.preventDefault();

    save.mutate(
      {
        preferredDays: days.length > 0 ? days : null,
        preferredTime: time,
        // The empty option is "nobody has asked", which is null on the wire.
        // The server accepts `''` too and normalises it; sending null is the
        // honest version of what the blank option means.
        preferredContactMethod: contactMethod === '' ? null : contactMethod,
        specialRequirements: requirements,
        petNotes: pets,
        allergyNotes: allergies,
      },
      {
        onSuccess: () => {
          toast.success('Preferences saved.');
        },
      },
    );
  }

  return (
    <form
      onSubmit={submit}
      className="space-y-5 rounded-lg border border-ink-300 bg-white p-4 shadow-card"
    >
      {error ? <FormError>{error.message}</FormError> : null}

      <fieldset>
        <legend className="text-label text-ink-900">Preferred days</legend>
        <div className="mt-2 flex flex-wrap gap-2">
          {WEEKDAY_LABELS.map((label, day) => {
            const selected = days.includes(day);
            return (
              <button
                key={label}
                type="button"
                role="checkbox"
                aria-checked={selected}
                onClick={() => {
                  toggleDay(day);
                }}
                className={cn(
                  'inline-flex min-h-touch items-center rounded-full px-3.5 text-label',
                  selected
                    ? 'bg-honey-400 text-ink-900'
                    : 'border border-ink-300 bg-white text-ink-700 hover:bg-honey-50',
                )}
              >
                {label.slice(0, 3)}
                <span className="sr-only">{label}</span>
              </button>
            );
          })}
        </div>
        <p className="mt-1.5 text-meta text-ink-500">
          Nothing selected means no preference, which is different from every day.
        </p>
      </fieldset>

      <Field
        label="Preferred start time"
        name="preferredTime"
        type="time"
        value={time}
        onChange={(event) => {
          setTime(event.target.value);
        }}
        className="sm:w-48"
        hint="A wall-clock time — 9am means 9am in Cedar City on every date."
      />

      {/*
        **"Not asked" is the default and it is a real answer**, distinct from
        "no preference". Defaulting somebody to email would be quietly wrong
        about a customer who only ever answers a text.
      */}
      <div className="space-y-1.5">
        <Label htmlFor="client-contact-method">Preferred way to reach them</Label>
        <select
          id="client-contact-method"
          name="preferredContactMethod"
          value={contactMethod}
          onChange={(event) => {
            setContactMethod(event.target.value as ContactMethodValue | '');
          }}
          className="min-h-touch w-full rounded-sm border border-ink-300 bg-white px-3 text-body sm:w-64"
        >
          <option value="">Nobody has asked</option>
          {CONTACT_METHOD_VALUES.map((value) => (
            <option key={value} value={value}>
              {CONTACT_METHOD_LABELS[value]}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="client-requirements">Special requirements</Label>
        <textarea
          id="client-requirements"
          name="specialRequirements"
          rows={3}
          value={requirements}
          onChange={(event) => {
            setRequirements(event.target.value);
          }}
          className="w-full rounded-sm border border-ink-300 bg-white px-3 py-2 text-body"
          placeholder="Unscented products only. Do not move anything on the desk."
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="client-pets">Pets</Label>
        <textarea
          id="client-pets"
          name="petNotes"
          rows={2}
          value={pets}
          onChange={(event) => {
            setPets(event.target.value);
          }}
          className="w-full rounded-sm border border-ink-300 bg-white px-3 py-2 text-body"
          placeholder="Two cats, indoor only. Don't let them out the back."
        />
        <p className="text-meta text-ink-500">
          A cleaner reads this before opening a door. Worth being specific.
        </p>
      </div>

      {/*
        **Not a preference — a safety fact**, which is why it sits beside pets
        rather than up with preferred days. It travels the same way: to the
        cleaner who is about to use a product in somebody's home.
      */}
      <div className="space-y-1.5">
        <Label htmlFor="client-allergies">Allergies and sensitivities</Label>
        <textarea
          id="client-allergies"
          name="allergyNotes"
          rows={2}
          value={allergies}
          onChange={(event) => {
            setAllergies(event.target.value);
          }}
          className="w-full rounded-sm border border-ink-300 bg-white px-3 py-2 text-body"
          placeholder="Severe reaction to bleach. Nothing with a fragrance in the nursery."
        />
        <p className="text-meta text-ink-500">
          This reaches the cleaner on the job, the same way pet notes do.
        </p>
      </div>

      <div className="flex justify-end">
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save preferences'}
        </Button>
      </div>
    </form>
  );
}
